import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["email", "bootstrapToken", "error", "submit"]
  static values = {
    actionType: String,
    optionsUrl: String,
    submitUrl: String
  }

  async submit(event) {
    event.preventDefault()
    this.showError("")

    if (!window.PublicKeyCredential) {
      this.showError("Passkeys are not supported in this browser.")
      return
    }

    this.setSubmitting(true)

    try {
      const email = this.hasEmailTarget ? this.emailTarget.value.trim() : ""
      const bootstrapToken = this.hasBootstrapTokenTarget ? this.bootstrapTokenTarget.value : ""
      if (this.hasEmailTarget && email.length === 0) {
        throw new Error("Email is required.")
      }

      const options = await this.fetchOptions(email, bootstrapToken)
      const credential = await this.createCredential(options)
      const payload = this.serializeCredential(credential)
      const result = await this.submitCredential(payload)

      window.location.href = result.redirect_url || "/admin/contacts"
    } catch (error) {
      this.showError(error.message || "Passkey request failed.")
    } finally {
      this.setSubmitting(false)
    }
  }

  async fetchOptions(email, bootstrapToken) {
    const response = await fetch(this.optionsUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ email, bootstrap_token: bootstrapToken })
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || "Could not start passkey flow.")
    }

    return this.normalizePublicKeyOptions(data)
  }

  async createCredential(options) {
    if (this.actionTypeValue === "create") {
      return navigator.credentials.create({ publicKey: options })
    }

    return navigator.credentials.get({ publicKey: options })
  }

  async submitCredential(credential) {
    const response = await fetch(this.submitUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ credential })
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || "Passkey verification failed.")
    }

    return data
  }

  normalizePublicKeyOptions(options) {
    const normalized = { ...options }
    normalized.challenge = this.base64urlToBuffer(options.challenge)

    if (normalized.user && normalized.user.id) {
      normalized.user = { ...normalized.user, id: this.base64urlToBuffer(normalized.user.id) }
    }

    if (Array.isArray(normalized.allowCredentials)) {
      normalized.allowCredentials = normalized.allowCredentials.map((credential) => ({
        ...credential,
        id: this.base64urlToBuffer(credential.id)
      }))
    }

    if (Array.isArray(normalized.excludeCredentials)) {
      normalized.excludeCredentials = normalized.excludeCredentials.map((credential) => ({
        ...credential,
        id: this.base64urlToBuffer(credential.id)
      }))
    }

    return normalized
  }

  serializeCredential(credential) {
    const base = {
      id: credential.id,
      rawId: this.bufferToBase64url(credential.rawId),
      type: credential.type,
      clientExtensionResults: credential.getClientExtensionResults()
    }

    if (credential.response.attestationObject) {
      return {
        ...base,
        response: {
          attestationObject: this.bufferToBase64url(credential.response.attestationObject),
          clientDataJSON: this.bufferToBase64url(credential.response.clientDataJSON)
        }
      }
    }

    return {
      ...base,
      response: {
        authenticatorData: this.bufferToBase64url(credential.response.authenticatorData),
        clientDataJSON: this.bufferToBase64url(credential.response.clientDataJSON),
        signature: this.bufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle ? this.bufferToBase64url(credential.response.userHandle) : null
      }
    }
  }

  base64urlToBuffer(base64url) {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
    const padding = "=".repeat((4 - (base64.length % 4)) % 4)
    const binary = window.atob(base64 + padding)
    const bytes = new Uint8Array(binary.length)

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }

    return bytes.buffer
  }

  bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""

    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i])
    }

    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
    }
  }

  setSubmitting(isSubmitting) {
    if (!this.hasSubmitTarget) return

    this.submitTarget.disabled = isSubmitting
    this.submitTarget.classList.toggle("opacity-60", isSubmitting)
    this.submitTarget.classList.toggle("cursor-not-allowed", isSubmitting)
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    if (message) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    } else {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }
}
